import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { setLogin, setLogout, setOfflineMode, setCrewRanks } from "state";
import api from 'state/axios';

const isTokenExpired = (token) => {
    try {
        return jwtDecode(token).exp < Date.now() / 1000;
    } catch {
        return true;
    }
};

const useAuthCheck = () => {
    const token      = useSelector((state) => state.auth.token);
    const offlineMode = useSelector((state) => state.auth.offlineMode);
    const dispatch   = useDispatch();
    const refreshing = useRef(false);

    // Token expiry check
    useEffect(() => {
        if (!token) {
            // No token and not in offline mode → logout
            if (!offlineMode) dispatch(setLogout());
            return;
        }
        // When offline: keep user logged in even if token expired
        if (!navigator.onLine) return;
        if (isTokenExpired(token)) dispatch(setLogout());
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-refresh real token when coming back online from offline mode
    useEffect(() => {
        if (!offlineMode || !navigator.onLine || refreshing.current) return;

        const tryRefresh = async () => {
            refreshing.current = true;
            try {
                const res = await api.get('/auth/refresh');
                const { token: newToken, crewMember } = res.data;
                const [paramsRes, ranksRes] = await Promise.all([
                    api.get('/params', { headers: { Authorization: `Bearer ${newToken}` } }),
                    api.get('/ranks/active', { headers: { Authorization: `Bearer ${newToken}` } }),
                ]);
                dispatch(setLogin({ user: crewMember, token: newToken, params: paramsRes.data }));
                dispatch(setCrewRanks({ crewRanks: ranksRes.data }));
                dispatch(setOfflineMode(false));
            } catch {
                // Refresh cookie also expired — force full re-login
                dispatch(setLogout());
            } finally {
                refreshing.current = false;
            }
        };

        const handleOnline = () => tryRefresh();
        window.addEventListener('online', handleOnline);
        // Also try immediately in case we're already online when this runs
        if (navigator.onLine) tryRefresh();

        return () => window.removeEventListener('online', handleOnline);
    }, [offlineMode]); // eslint-disable-line react-hooks/exhaustive-deps
};

export default useAuthCheck;
